import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Card,
  Chip,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

import LogicBuilderCard, {
  createEmptyGroup,
  extractSlotsFromLogic,
} from "./LogicBuilderCard";

/* -------------------------------------------------------
   Normalisation + helpers
---------------------------------------------------------*/

const norm = (str) =>
  (str || "")
    .toLowerCase()
    .replace(/[–—‐]/g, "-")
    .replace(/[^a-z0-9\s-]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const parseRange = (str) => {
  if (!str) return null;
  const cleaned = str.replace(/[^\d-]/g, " ");
  const nums = cleaned
    .split(/[^0-9]+/)
    .filter(Boolean)
    .map((n) => parseInt(n, 10));
  if (nums.length === 2) return { min: nums[0], max: nums[1] };
  return null;
};

/* -------------------------------------------------------
   Normalise percentages (per section)
---------------------------------------------------------*/
const normalizePercentages = (rows) => {
  const positiveTotal = rows
    .filter((r) => r.pct > 0)
    .reduce((sum, r) => sum + r.pct, 0);
  if (positiveTotal === 0) return rows;
  return rows.map((r) => ({
    ...r,
    pct: r.pct > 0 ? (r.pct / positiveTotal) * 100 : 0,
  }));
};

/* -------------------------------------------------------
   Score a single label part against one option
---------------------------------------------------------*/
const scorePartVsOption = (part, optTitle) => {
  const partNorm = norm(part);
  const partTokens = partNorm.split(" ");
  const partRange = parseRange(part);
  const optNorm = norm(optTitle);
  const optRange = parseRange(optTitle);

  let score = 0;
  if (optNorm === partNorm) score = 4;
  if (partTokens.includes(optNorm)) score = Math.max(score, 3);
  if (
    partRange &&
    optRange &&
    partRange.min === optRange.min &&
    partRange.max === optRange.max
  )
    score = Math.max(score, 3.5);
  if (optNorm.split(" ").some((w) => partTokens.includes(w)))
    score = Math.max(score, 1);
  return score;
};

/* Find the single best-match option id for a label part (requires score >= 3, unique best) */
const findBestOptionMatch = (part, options) => {
  let bestScore = 0, bestId = null, bestCount = 0;
  options.forEach((opt) => {
    const s = scorePartVsOption(part, opt.title?.English ?? opt.title ?? "");
    if (s > 0) {
      if (s > bestScore) { bestScore = s; bestId = opt.id; bestCount = 1; }
      else if (s === bestScore) bestCount++;
    }
  });
  return bestScore >= 3 && bestCount === 1 ? bestId : null;
};

/* -------------------------------------------------------
   Scoring-based autofill (single best match, full-label mode)
   Used only for non-absolute (pct) sections
---------------------------------------------------------*/
const autoFillFor = (rows, setRows, slots, questions) => {
  const updated = rows.map((row) => {
    const labelNorm = norm(row.label);
    const labelTokens = labelNorm.split(" ");
    const newSlotValues = { ...row.slotValues };

    slots.forEach((slot) => {
      if (
        newSlotValues[slot.slotId] &&
        Array.isArray(newSlotValues[slot.slotId]) &&
        newSlotValues[slot.slotId].length > 0
      )
        return;

      const q = questions.find((q) => q.id === slot.questionId);
      if (!q || !q.options) return;

      let bestScore = 0, bestOptionId = null, bestCount = 0;
      const labelRange = parseRange(row.label);

      q.options.forEach((opt) => {
        const raw = opt.title?.English ?? opt.title ?? "";
        const titleNorm = norm(raw);
        const optionRange = parseRange(raw);
        let score = 0;

        if (titleNorm === labelNorm) score = 4;
        if (labelTokens.includes(titleNorm)) score = Math.max(score, 3);
        if (
          labelRange &&
          optionRange &&
          labelRange.min === optionRange.min &&
          labelRange.max === optionRange.max
        )
          score = Math.max(score, 3.5);
        if (raw.split(" ").some((w) => labelTokens.includes(norm(w))))
          score = Math.max(score, 1);

        if (score > 0) {
          if (score > bestScore) { bestScore = score; bestOptionId = opt.id; bestCount = 1; }
          else if (score === bestScore) bestCount++;
        }
      });

      if (bestScore > 0 && bestCount === 1 && bestOptionId != null)
        newSlotValues[slot.slotId] = [bestOptionId];
    });

    return { ...row, slotValues: newSlotValues };
  });

  setRows(updated);
};

/* -------------------------------------------------------
   Logic → text preview
---------------------------------------------------------*/
const logicToString = (node, slotLookup, questionLookup) => {
  if (!node) return "";
  if (node.type === "SLOT") {
    const slot = slotLookup[node.slotId];
    if (!slot) return node.slotId;
    const q = questionLookup[slot.questionId];
    return q ? `[${q.title?.English ?? q.title}]` : `[${slot.slotId}]`;
  }
  if (node.type === "GROUP") {
    const op = ` ${node.op} `;
    const parts = node.children.map((c) => logicToString(c, slotLookup, questionLookup));
    return "(" + parts.join(op) + ")";
  }
  return "";
};

/* -------------------------------------------------------
   MAIN COMPONENT
---------------------------------------------------------*/
export default function QuotaSection({
  title,
  initialRows,
  value,
  onChange,
  questions,
  overallN,
  allowAddRows = false,
  showDescription = false,
  useAbsoluteLimits = false,
  rowsPerPage = null,
}) {
  const [logicTemplate, setLogicTemplate] = useState(
    value?.logicTemplate || createEmptyGroup("AND")
  );
  const [quotaRows, setQuotaRows] = useState(value?.quotaRows || initialRows);
  const [description, setDescription] = useState(value?.description || "");
  const [slotMappings, setSlotMappings] = useState(value?.slotMappings || {});
  const [page, setPage] = useState(0);
  // Incrementing this counter forces re-initialization of slot mappings
  const [initTrigger, setInitTrigger] = useState(0);

  const slots = useMemo(() => extractSlotsFromLogic(logicTemplate), [logicTemplate]);

  /* ---- Unique label parts (split by " x ") ----
     Derive a stable string key from labels only so that changes to slotValues
     (which don't affect labels) don't cause this memo to recompute and trigger
     the init-mappings effect, breaking the dependency cycle. */
  const labelKey = useMemo(
    () => quotaRows.map((r) => r.label).join("\0"),
    [quotaRows]
  );

  const uniqueLabelParts = useMemo(() => {
    const parts = new Set();
    labelKey.split("\0").forEach((label) =>
      label.split(" x ").forEach((p) => { if (p) parts.add(p.trim()); })
    );
    return [...parts].sort();
  }, [labelKey]);

  /* ---- For each slot: all unique label parts, sorted with auto-matched ones first ----
     We show ALL parts (not just scoring ones) so users can manually set values that
     don't textually match the survey's question options. */
  const relevantPartsPerSlot = useMemo(() => {
    const result = {};
    slots.forEach((slot) => {
      const q = questions.find((q) => q.id === slot.questionId);
      if (!q) { result[slot.slotId] = uniqueLabelParts; return; }
      // Sort: parts that score > 0 against this question's options come first
      result[slot.slotId] = [...uniqueLabelParts].sort((a, b) => {
        const scoreA = q.options.some(
          (opt) => scorePartVsOption(a, opt.title?.English ?? opt.title ?? "") > 0
        );
        const scoreB = q.options.some(
          (opt) => scorePartVsOption(b, opt.title?.English ?? opt.title ?? "") > 0
        );
        if (scoreA === scoreB) return a.localeCompare(b);
        return scoreB ? 1 : -1; // scored parts first
      });
    });
    return result;
  }, [slots, questions, uniqueLabelParts]);

  /* ---- Autofill (pct mode only, deferred) ---- */
  useEffect(() => {
    if (useAbsoluteLimits) return;
    const tid = setTimeout(() => {
      autoFillFor(quotaRows, setQuotaRows, slots, questions);
    }, 0);
    return () => clearTimeout(tid);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slots, questions]);

  /* ---- Initialize slot mappings (absolute mode only) ---- */
  useEffect(() => {
    if (!useAbsoluteLimits) return;
    if (!slots.length || !questions.length || !uniqueLabelParts.length) return;

    setSlotMappings((prev) => {
      const updated = { ...prev };
      let changed = false;
      slots.forEach((slot) => {
        // On forced reset (initTrigger > 0) or if slot is new: (re-)initialize
        if (updated[slot.slotId] !== undefined && initTrigger === 0) return;
        updated[slot.slotId] = {};
        const q = questions.find((q) => q.id === slot.questionId);
        if (q) {
          uniqueLabelParts.forEach((part) => {
            const match = findBestOptionMatch(part, q.options);
            if (match) updated[slot.slotId][part] = [match];
          });
        }
        changed = true;
      });
      // Return the same reference if nothing changed so apply-mappings doesn't re-fire
      return changed ? updated : prev;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slots, questions, uniqueLabelParts, initTrigger]);

  /* ---- Apply slot mappings to all rows ---- */
  useEffect(() => {
    if (!useAbsoluteLimits) return;
    if (!Object.keys(slotMappings).length) return;

    setQuotaRows((prev) => {
      const next = prev.map((row) => {
        const parts = row.label.split(" x ").map((p) => p.trim());
        const newSlotValues = {};
        slots.forEach((slot) => {
          for (const part of parts) {
            const optIds = slotMappings[slot.slotId]?.[part];
            if (optIds?.length) { newSlotValues[slot.slotId] = optIds; break; }
          }
        });
        // Only create a new row object if slotValues actually changed
        const prev_sv = row.slotValues || {};
        const allKeys = new Set([...Object.keys(prev_sv), ...Object.keys(newSlotValues)]);
        const same = [...allKeys].every(
          (k) => JSON.stringify(prev_sv[k] ?? []) === JSON.stringify(newSlotValues[k] ?? [])
        );
        return same ? row : { ...row, slotValues: newSlotValues };
      });
      // If every row is the same reference, return prev to avoid triggering downstream effects
      return next.every((r, i) => r === prev[i]) ? prev : next;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slotMappings, slots]);

  /* ---- Propagate state to parent ---- */
  useEffect(() => {
    onChange({ logicTemplate, slots, quotaRows, description, slotMappings });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [logicTemplate, slots, quotaRows, description, slotMappings]);

  /* ---- Lookups for logic preview ---- */
  const slotLookup = useMemo(
    () => Object.fromEntries(slots.map((s) => [s.slotId, s])),
    [slots]
  );
  const questionLookup = useMemo(
    () => Object.fromEntries(questions.map((q) => [q.id, q])),
    [questions]
  );
  const preview = logicToString(logicTemplate, slotLookup, questionLookup);

  const getQuestionById = (id) => questions.find((q) => q.id === id);
  const calculateTargetN = (pct) => Math.round((overallN * pct) / 100);

  /* ---- Row handlers ---- */
  const handleRowPctChange = (i, val) => {
    const updated = [...quotaRows];
    updated[i].pct = Number(val);
    setQuotaRows(updated);
  };

  const handleRowLimitChange = (i, val) => {
    const updated = [...quotaRows];
    updated[i].limit = Math.round(Number(val)) || 0;
    setQuotaRows(updated);
  };

  const handleNormalise = () => setQuotaRows((rows) => normalizePercentages(rows));

  const handleAddRow = () =>
    setQuotaRows((rows) => [...rows, { label: "", pct: 0, slotValues: {} }]);

  const handleRemoveRow = (i) =>
    setQuotaRows((rows) => rows.filter((_, idx) => idx !== i));

  const handleRowLabelChange = (i, val) => {
    const updated = [...quotaRows];
    updated[i] = { ...updated[i], label: val };
    setQuotaRows(updated);
  };

  const handleResetMappings = () => setInitTrigger((t) => t + 1);

  /* ---- Slot mapping change ---- */
  const handleMappingChange = useCallback((slotId, part, optionIds) => {
    setSlotMappings((prev) => ({
      ...prev,
      [slotId]: { ...(prev[slotId] || {}), [part]: optionIds.length ? optionIds : undefined },
    }));
  }, []);

  /* ---- Pagination ---- */
  const rowOffset = rowsPerPage ? page * rowsPerPage : 0;
  const visibleRows = rowsPerPage
    ? quotaRows.slice(rowOffset, rowOffset + rowsPerPage)
    : quotaRows;
  const totalPages = rowsPerPage ? Math.ceil(quotaRows.length / rowsPerPage) : 1;

  return (
    <Card variant="outlined" sx={{ p: 3, width: "100%", mb: 4 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>
        {title}
      </Typography>

      {showDescription && (
        <TextField
          label="Description (optional)"
          size="small"
          fullWidth
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          sx={{ mb: 3 }}
        />
      )}

      {/* LOGIC BUILDER */}
      <LogicBuilderCard
        value={logicTemplate}
        onChange={setLogicTemplate}
        questions={questions}
      />

      {/* LOGIC PREVIEW */}
      <Typography variant="subtitle2" sx={{ mt: 2 }}>
        Logic summary:
      </Typography>
      <Typography
        sx={{
          fontFamily: "monospace",
          whiteSpace: "pre-wrap",
          background: "#f6f6f6",
          p: 2,
          mb: 3,
          borderRadius: 1,
        }}
      >
        {preview}
      </Typography>

      {/* -------------------------------------------------------
          VALUE MAPPINGS (absolute limits / bulk mode only)
          Set each unique label part once → applied to all rows
      ------------------------------------------------------- */}
      {useAbsoluteLimits && slots.length > 0 && (
        <Box sx={{ mb: 3, p: 2, bgcolor: "grey.50", borderRadius: 2 }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ mb: 1 }}
          >
            <Typography variant="subtitle1" fontWeight={600}>
              Value mappings
            </Typography>
            <Button size="small" variant="outlined" onClick={handleResetMappings}>
              Re-run auto-fill
            </Button>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Set each unique label value once — automatically applied to all rows.
          </Typography>

          {slots.map((slot) => {
            const q = getQuestionById(slot.questionId);
            const options = q?.options || [];
            const parts = relevantPartsPerSlot[slot.slotId] || [];
            if (!parts.length) return null;

            return (
              <Box key={slot.slotId} sx={{ mb: 2.5 }}>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: 0.6,
                    color: "text.secondary",
                    display: "block",
                    mb: 1,
                  }}
                >
                  {slot.slotId}
                  {q ? ` → ${q.title?.English ?? q.title}` : " (no question assigned)"}
                </Typography>

                <Grid container spacing={1}>
                  {parts.map((part) => {
                    const currentVal = slotMappings[slot.slotId]?.[part] || [];
                    const matched = currentVal.length > 0;
                    return (
                      <Grid item xs={12} sm={6} md={4} key={part}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Typography
                            variant="body2"
                            sx={{
                              minWidth: 130,
                              maxWidth: 130,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              color: matched ? "text.primary" : "text.disabled",
                              fontWeight: matched ? 500 : 400,
                            }}
                            title={part}
                          >
                            {part}
                          </Typography>
                          <FormControl size="small" fullWidth disabled={!q}>
                            <Select
                              multiple
                              displayEmpty
                              value={currentVal}
                              onChange={(e) =>
                                handleMappingChange(slot.slotId, part, e.target.value)
                              }
                              renderValue={(vals) => {
                                if (!vals || vals.length === 0)
                                  return <em style={{ color: "#bbb" }}>— unset —</em>;
                                return vals
                                  .map((id) => {
                                    const opt = options.find((o) => o.id === id);
                                    return opt?.title?.English ?? opt?.title ?? id;
                                  })
                                  .join(", ");
                              }}
                              sx={{
                                bgcolor: matched ? "white" : "transparent",
                              }}
                            >
                              {options.map((opt) => (
                                <MenuItem key={opt.id} value={opt.id}>
                                  {opt.title?.English ?? opt.title}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Stack>
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>
            );
          })}
        </Box>
      )}

      {/* QUOTA TABLE HEADER */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 2 }}
      >
        <Typography variant="h6">Quota cells</Typography>

        <Stack direction="row" spacing={1}>
          {allowAddRows && (
            <Button variant="outlined" size="small" onClick={handleAddRow}>
              Add row
            </Button>
          )}
          {!useAbsoluteLimits && (
            <Button variant="outlined" size="small" onClick={handleNormalise}>
              Normalise %
            </Button>
          )}
        </Stack>
      </Stack>

      {/* QUOTA TABLE */}
      <Grid container spacing={2}>
        {visibleRows.map((row, localIdx) => {
          const rowIdx = rowOffset + localIdx;
          const allMapped =
            slots.length > 0 &&
            slots.every((s) => row.slotValues?.[s.slotId]?.length > 0);

          return (
            <Grid
              key={rowIdx}
              item
              xs={12}
              sx={{ borderBottom: "1px solid #eee", pb: 2, mb: 2 }}
            >
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ mb: 1 }}
              >
                {allowAddRows ? (
                  <TextField
                    size="small"
                    label="Label"
                    value={row.label}
                    onChange={(e) => handleRowLabelChange(rowIdx, e.target.value)}
                    sx={{ width: 240 }}
                  />
                ) : (
                  <Typography variant="subtitle1" fontWeight={600}>
                    {row.label}
                  </Typography>
                )}
                {allowAddRows && (
                  <IconButton
                    size="small"
                    onClick={() => handleRemoveRow(rowIdx)}
                    color="error"
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                )}
              </Stack>

              <Grid container spacing={2} alignItems="center">
                {useAbsoluteLimits ? (
                  /* Absolute mode: target N + mapping status badge */
                  <>
                    <Grid item xs={6} sm={3}>
                      <TextField
                        label="Target N"
                        type="number"
                        size="small"
                        fullWidth
                        value={row.limit ?? 0}
                        onChange={(e) => handleRowLimitChange(rowIdx, e.target.value)}
                      />
                    </Grid>
                    {slots.length > 0 && (
                      <Grid item xs={6} sm="auto">
                        <Chip
                          label={allMapped ? "✓ mapped" : "unmapped"}
                          color={allMapped ? "success" : "warning"}
                          size="small"
                          variant="outlined"
                        />
                      </Grid>
                    )}
                  </>
                ) : (
                  <>
                    {/* Percent */}
                    <Grid item xs={12} sm={3}>
                      <TextField
                        label="Percentage (%)"
                        type="number"
                        size="small"
                        fullWidth
                        value={row.pct}
                        onChange={(e) => handleRowPctChange(rowIdx, e.target.value)}
                      />
                    </Grid>

                    {/* Target N */}
                    <Grid item xs={12} sm={2}>
                      <Typography variant="body2">Target N</Typography>
                      <Typography sx={{ mt: "7px" }}>
                        {calculateTargetN(row.pct)}
                      </Typography>
                    </Grid>

                    {/* Slot dropdowns (pct mode only) */}
                    {slots.map((slot) => {
                      const q = getQuestionById(slot.questionId);
                      const options = q?.options || [];
                      const selectedArray = Array.isArray(row.slotValues?.[slot.slotId])
                        ? row.slotValues[slot.slotId]
                        : [];

                      return (
                        <Grid item xs={12} sm={3} key={slot.slotId}>
                          <FormControl fullWidth size="small" disabled={!q}>
                            <InputLabel>{slot.slotId}</InputLabel>
                            <Select
                              multiple
                              label={slot.slotId}
                              value={selectedArray}
                              onChange={(e) => {
                                const arr = e.target.value;
                                const updated = [...quotaRows];
                                updated[rowIdx].slotValues = {
                                  ...updated[rowIdx].slotValues,
                                  [slot.slotId]: arr,
                                };
                                setQuotaRows(updated);
                              }}
                              renderValue={(selected) =>
                                selected
                                  .map((id) => {
                                    const opt = options.find((o) => o.id === id);
                                    return opt?.title?.English ?? opt?.title ?? "";
                                  })
                                  .join(", ")
                              }
                            >
                              <MenuItem value="">
                                <em>Select option(s)</em>
                              </MenuItem>
                              {options.map((opt) => (
                                <MenuItem key={opt.id} value={opt.id}>
                                  {opt.title?.English ?? opt.title}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                      );
                    })}
                  </>
                )}
              </Grid>
            </Grid>
          );
        })}
      </Grid>

      {/* Pagination controls */}
      {rowsPerPage && totalPages > 1 && (
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="center"
          spacing={2}
          sx={{ mt: 2 }}
        >
          <Button
            size="small"
            variant="outlined"
            disabled={page === 0}
            onClick={() => setPage((p) => p - 1)}
          >
            Previous
          </Button>
          <Typography variant="body2" color="text.secondary">
            {rowOffset + 1}–{Math.min(rowOffset + rowsPerPage, quotaRows.length)} of{" "}
            {quotaRows.length}
          </Typography>
          <Button
            size="small"
            variant="outlined"
            disabled={page >= totalPages - 1}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </Button>
        </Stack>
      )}
    </Card>
  );
}
